import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { FilterOption, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { State } from '@/model/State.model';
import * as Auth from '@/support/auth';
import { useFetchContext } from '@/support/Hooks/fetch';
import { RouteService } from '@/support/RouteService';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  plannedAnimalBulkActions,
  plannedAnimalColumns,
  plannedAnimalFilterOptions,
  type PlannedAnimalWithGroup,
  updateGroupCapacities,
} from './PlannedAnimals.utils';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import { useModalAction } from '@/utils/modal.tsx';
import { _isNil, _isNotEmpty, _notNil, uuid } from '@/littledash.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService.ts';
import { RiAlertFill } from 'react-icons/ri';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { type AnimalDefinitionV1 } from '@/model/Animal.model.ts';
import {
  type AnimalCreateV1,
  type AnimalDefinitionApiId,
  type AnimalDefinitionUpsertV1,
  type AnimalMapV1,
} from '@/model/PlannedAnimal.model';
import Button from '@/components/UI/Button';
import type { AnimalsContextProps } from '@/components/Animals/Animals.tsx';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import { notAborted } from '@/support/Hooks/fetch/useAbortController.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import Loading from '@/components/Loading';

const featureSelector = createSelector([featuresSelector], (features) => ({
  atunesIntegrationEnabled: features?.atunes_colony_management_integration ?? false,
}));

const checkGroupsValid = (
  groups: Array<TreatmentGroup>,
  plannedAnimals: Array<PlannedAnimalWithGroup>
): string | undefined => {
  const groupIds = new Set(groups.map((group) => group.api_id as GroupApiId));
  const invalidPlannedAnimals = plannedAnimals.reduce<Array<AnimalDefinitionV1['planned_id']>>((acc, animal) => {
    if (_isNil(animal.study_group_api_id) || !groupIds.has(animal.study_group_api_id as GroupApiId)) {
      acc.push(animal.planned_id);
    }
    return acc;
  }, []);
  const availableCapacity = groups.reduce((total, group) => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const availableGroupCapacity = (group.max_subjects || 0) - (group?.animals_count || 0);
    return total + Math.max(0, availableGroupCapacity);
  }, 0);
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const availablePlannedAnimals = plannedAnimals.filter((animal) => !animal.animal_api_id).length;
  if (invalidPlannedAnimals.length > 0) {
    return `Some planned animals are assigned to invalid groups: ${[...new Set(invalidPlannedAnimals)].join(', ')}`;
  }
  if (availableCapacity <= 0 && availablePlannedAnimals > 0) {
    return 'All treatment groups are at full capacity';
  }
  if (availableCapacity < availablePlannedAnimals) {
    return 'Not enough available capacity in treatment groups for all planned animals';
  }
  return undefined;
};

const PlannedAnimalsTable: FC = () => {
  const [columns, setColumns] = useState<Array<TanStackColumnDef<PlannedAnimalWithGroup>>>([]);
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [apiError, setApiError] = useState<unknown | false>(false);
  const [groupsError, setGroupsError] = useState<string | undefined>();
  const { entity: study, metadataOptions, speciesData } = useFetchContext<AnimalsContextProps>();
  const [groupsLoading, setGroupsLoading] = useState<boolean>(true);
  const [groupsData, setGroupsData] = useState<Array<TreatmentGroup>>([]);
  const [hasAnimals, setHasAnimals] = useState<boolean>(false);
  const [plannedAnimalsData, setPlannedAnimalsData] = useState<Array<PlannedAnimalWithGroup>>([]);
  const [refetchToken, setRefetchToken] = useState<string>(uuid());
  const [selectedAnimalDefinitions, updateSelectedAnimalDefinitions] = useState<Set<AnimalDefinitionApiId>>(new Set());
  const dispatch = useDispatch();
  const { openModal } = useModalAction();

  const settings = useSelector((state: State) => state.ui.settings);
  const { atunesIntegrationEnabled } = useSelector(featureSelector);
  const unmappedPlannedAnimals: Array<PlannedAnimalWithGroup> = useMemo(() => {
    return plannedAnimalsData.filter((animal) => !animal.animal_api_id);
  }, [plannedAnimalsData]);

  const { invoke: fetchGroups } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: study.id },
    query: { perPage: -1, include: ['animals'] },
    options: { invokeOnInit: false, onError: { slug: 'fetch-study-groups', throw: false } },
  });

  const initialise = async () => {
    try {
      const groupsResponse = await fetchGroups();
      let groups: Array<TreatmentGroup> = [];
      if (groupsResponse.type === 'success') {
        groups = ((groupsResponse?.body as { data: Array<TreatmentGroup> })?.data ?? []).map((g) => ({
          ...g,
          animals_count: g.animals?.length ?? 0,
        }));
        setGroupsData(groups);
        if (groups?.some((group) => (group.animals_count ?? 0) > 0)) {
          setHasAnimals(true);
        }
      }
      setFilterOptions(plannedAnimalFilterOptions(groups, speciesData));
      const columns = plannedAnimalColumns(metadataOptions, study.id);
      setColumns(columns);
    } catch (error) {
      if (notAborted(error)) {
        setApiError(error);
      }
    } finally {
      setGroupsLoading(false);
    }
  };

  useEffect(() => {
    if (_notNil(study?.id)) {
      initialise();
    }
  }, [study?.id]);

  useEffect(() => {
    if (_isNotEmpty(groupsData) && _isNotEmpty(plannedAnimalsData)) {
      setGroupsError(checkGroupsValid(groupsData, plannedAnimalsData));
    }
  }, [groupsData, plannedAnimalsData]);

  const hasAccess = Auth.isWriteUserForStudy(study);
  const bulkActions: (props: UseTableProps<PlannedAnimalWithGroup>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }) => {
    if (hasAccess) {
      const hasReviewOutstanding = settings.studyState?.pageStates?.animals;
      const hasNoUnmappedPlannedAnimals = unmappedPlannedAnimals.length === 0;

      const customActions = [
        ...(!atunesIntegrationEnabled
          ? [
              {
                name: 'Create from planned animals',
                action: () => handleCreateFromPlannedAnimals(),
                disabled: hasNoUnmappedPlannedAnimals || hasReviewOutstanding,
                tooltip: hasReviewOutstanding
                  ? 'Pending review must be completed'
                  : hasNoUnmappedPlannedAnimals
                    ? 'Cannot create from planned animals which are already mapped'
                    : undefined,
              },
            ]
          : []),
        {
          name: 'Edit planned animals',
          action: () => handleEditPlannedAnimals(),
          disabled: hasNoUnmappedPlannedAnimals,
          tooltip: hasNoUnmappedPlannedAnimals ? 'Cannot edit planned animals which are already mapped' : undefined,
        },
      ];

      const defaultActions = plannedAnimalBulkActions(selectedFlatRows, refetchTableData, study, dispatch);

      return [...customActions, ...defaultActions];
    } else {
      return [];
    }
  };

  const handleCreateFromPlannedAnimals = useCallback(() => {
    openModal('CREATE_ANIMALS', {
      plannedAnimals: plannedAnimalsData,
      selectedIds: selectedAnimalDefinitions,
      studyId: study?.id,
      studyApiId: study?.api_id,
      onSubmit: async (animals: AnimalCreateV1) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/create-animals',
            path: { studyApiId: study?.api_id },
            body: animals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully created animals');
          setHasAnimals(true);
          const animalsCreated: Array<AnimalDefinitionApiId> = animals.definitions.map((a) => a.api_id);
          const updatedGroups = updateGroupCapacities(animalsCreated, plannedAnimalsData, groupsData);
          setGroupsData(updatedGroups);
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not create animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not create animals', {
              cause: err,
              slug: 'create-from-planned-animals',
            })
          );
        }
      },
      editing: false,
    });
  }, [selectedAnimalDefinitions, plannedAnimalsData, study]);

  const handleEditPlannedAnimals = useCallback(() => {
    openModal('CREATE_ANIMALS', {
      plannedAnimals: plannedAnimalsData,
      selectedIds: selectedAnimalDefinitions,
      studyId: study?.id,
      studyApiId: study?.api_id,
      onSubmit: async (plannedAnimals: Array<AnimalDefinitionUpsertV1>) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/upsert',
            path: { studyApiId: study?.api_id },
            body: plannedAnimals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully updated planned animals');
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not edit planned animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not edit planned animals', {
              cause: err,
              slug: 'edit-planned-animals',
            })
          );
        }
      },
      editing: true,
    });
  }, [selectedAnimalDefinitions, plannedAnimalsData, study]);

  const onRowSelectionChange = useCallback((selectedRows: Array<unknown>) => {
    updateSelectedAnimalDefinitions(
      new Set((selectedRows as Array<{ original: AnimalDefinitionV1 }>)?.map((row) => row.original.api_id) ?? [])
    );
  }, []);

  const handleMapPlannedAnimals = useCallback(() => {
    openModal('MAP_ANIMALS', {
      studyApiId: study?.api_id,
      plannedAnimals: unmappedPlannedAnimals,
      groups: groupsData,
      onSubmit: async (mappedAnimals: AnimalMapV1) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/map-animals',
            path: { studyApiId: study?.api_id },
            body: mappedAnimals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully mapped planned animals');
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not map planned animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not map planned animals', {
              cause: err,
              slug: 'map-planned-animals',
            })
          );
        }
      },
    });
  }, [study?.api_id, unmappedPlannedAnimals, groupsData]);

  const isMapAnimalsDisabled = useMemo(() => {
    const hasReviewOutstanding = settings.studyState?.pageStates?.animals;
    const hasNoUnmappedPlannedAnimals = unmappedPlannedAnimals.length === 0;

    return hasNoUnmappedPlannedAnimals || hasReviewOutstanding || !hasAnimals;
  }, [unmappedPlannedAnimals, settings.studyState?.pageStates?.animals, hasAnimals]);

  const mapAnimalsTooltip = useMemo(() => {
    const hasReviewOutstanding = settings.studyState?.pageStates?.animals;
    const hasNoUnmappedPlannedAnimals = unmappedPlannedAnimals.length === 0;

    if (hasReviewOutstanding) {
      return 'Pending review must be completed';
    }
    if (hasNoUnmappedPlannedAnimals) {
      return 'No planned animals available';
    }
    if (!hasAnimals) {
      return 'No registered animals assigned to groups';
    }
    return undefined;
  }, [unmappedPlannedAnimals, settings.studyState?.pageStates?.animals, hasAnimals]);

  const handleFetchTableData = useCallback((data: Array<PlannedAnimalWithGroup>) => {
    setPlannedAnimalsData(data);
  }, []);

  return (
    <div className="pb4" data-test-component="PlannedAnimalsTable" data-test-element="planned-animals-table-container">
      {groupsLoading ? (
        <div style={{ height: 650 }}>
          <Loading txt="Loading planned animals..." />
        </div>
      ) : (
        <>
          {apiError ? (
            <ApiErrorBanner
              className="mw6 mb4"
              error={apiError}
              title="There was an error fetching animals data"
              text="An error has occurred when fetching animals data, please try again. If this keeps occurring please contact support."
            />
          ) : null}
          <APITable
            apiInfo={{
              type: 'internalApi',
              route: RouteService.api({
                endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
                path: { studyApiId: study?.api_id },
              }).url,
            }}
            includeParam="study_group, species_name, target_animal"
            columns={columns}
            bulkActions={bulkActions}
            defaultSortBy={{ id: 'planned_id', desc: false }}
            reduxTableName="planned_animals"
            showMetricChangeFrom={false}
            pageSizes={[50, 100, 200, 500, 1000]}
            defaultPageSize={1000}
            filterOptions={filterOptions}
            refetch={refetchToken}
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(metadataOptions ?? [])}
            onSelectRow={onRowSelectionChange}
            onFetchTableData={handleFetchTableData}
            AsideComponent={() => (
              <>
                {_isNotEmpty(groupsError) && (
                  <div style={{ display: 'flex' }}>
                    <RiAlertFill className="red" size={25} />
                    <span className="pl2">{groupsError}</span>
                  </div>
                )}
                {hasAccess && (
                  <Button
                    onClick={handleMapPlannedAnimals}
                    disabled={isMapAnimalsDisabled}
                    tooltip={mapAnimalsTooltip}
                    testId="map-animals-button"
                  >
                    Map animals
                  </Button>
                )}
              </>
            )}
          />
        </>
      )}
    </div>
  );
};

export default PlannedAnimalsTable;
